import React, { useEffect, useRef } from 'react';

import {
  Employee,
  employeeCoordinate,
  getPriorityName,
  Priority,
  TaskStatusCoordinateDetails,
  TaskStatusTransitionCoordinate
} from 'types/interfaces';
import { formatDateStringFromRaw } from 'utils/date-utils';
import { loadMarkerCluster } from 'utils/loadMarkerCluster';

interface OrderedTransition extends TaskStatusTransitionCoordinate {
  order: number;
  latitude: number;
  longitude: number;
}

interface TaskStatusMapProps {
  taskDetails?: TaskStatusCoordinateDetails | null;
  transitionsWithCoordinates: OrderedTransition[];
  employeesWithCoordinates: employeeCoordinate[];
  containerId: string;
  onShowEmployees?: (employees: Employee[]) => void;
  reservedRightWidth?: number;
}

const createTaskMarkerIcon = () => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 24 24" fill="#ff0000">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `;

  return window.DG.icon({
    iconUrl: 'data:image/svg+xml;base64,' + window.btoa(svg),
    iconSize: [28, 40],
    iconAnchor: [14, 36],
    popupAnchor: [0, -30]
  });
};

const createNumberedTransitionIcon = (order: number) => {
  const size = 38;
  const html = `
    <div style="
      width:${size}px;
      height:${size}px;
      border-radius:50%;
      background:linear-gradient(135deg,#0ea5e9,#22d3ee);
      color:#fff;
      font-weight:600;
      font-size:16px;
      display:flex;
      align-items:center;
      justify-content:center;
      border:2px solid #0f172a;
      box-shadow:0 2px 6px rgba(15,23,42,0.35);
    ">
      ${order}
    </div>
  `;

  return window.DG.divIcon({
    html,
    className: 'task-transition-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size / 2]
  });
};

const createEmployeeMarkerIcon = () => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#0000ff">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  `;

  return window.DG.icon({
    iconUrl: 'data:image/svg+xml;base64,' + window.btoa(svg),
    iconSize: [34, 34],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

const TaskStatusMap: React.FC<TaskStatusMapProps> = ({
  taskDetails,
  transitionsWithCoordinates,
  employeesWithCoordinates,
  containerId,
  onShowEmployees,
  reservedRightWidth = 0
}) => {
  const mapRef = useRef<any>(null);
  const employeeClusterRef = useRef<any>(null);
  const markersLayerRef = useRef<any>(null);
  const routeLayerRef = useRef<any>(null);

  useEffect(() => {
    if (!containerId) {
      return;
    }

    const initializeMap = () => {
      window.DG.then(() => loadMarkerCluster()).then(() => {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }

        mapRef.current = window.DG.map(containerId, {
          center: [55.751244, 37.618423],
          zoom: 11,
          key: 'b78aa276-8aa3-4a09-a608-4daeb2bb953c'
        });

        markersLayerRef.current = window.DG.layerGroup();
        markersLayerRef.current.addTo(mapRef.current);

        routeLayerRef.current = window.DG.layerGroup();
        routeLayerRef.current.addTo(mapRef.current);

        // Create employee cluster group
        employeeClusterRef.current = window.DG.markerClusterGroup({
          iconCreateFunction: function (cluster: any) {
            return window.DG.divIcon({
              html: `<span>${cluster.getChildCount()}</span>`,
              className: 'employee-cluster',
              iconSize: window.DG.point(40, 40)
            });
          }
        });
        employeeClusterRef.current.addTo(mapRef.current);

        const bounds = window.DG.latLngBounds();

        if (taskDetails?.asset?.latitude && taskDetails?.asset?.longitude) {
          const priority = taskDetails.priority
            ? getPriorityName(taskDetails.priority as Priority)
            : 'Низкий';
          const employeesPreview =
            taskDetails.employees && taskDetails.employees.length > 0
              ? taskDetails.employees
                  .slice(0, 3)
                  .map((emp) => emp.fullName)
                  .join(', ')
              : '—';
          const extraEmployees =
            taskDetails.employees && taskDetails.employees.length > 3
              ? `<span style="color:#64748B;"> и ещё ${
                  taskDetails.employees.length - 3
                }</span><button class="marker-btn cursor-pointer employees-btn" style="margin-left:8px;background:#0ea5e9;color:#fff;border:none;padding:4px 10px;border-radius:6px;">Сотрудники</button>`
              : '';
          const label = `
            <div style="min-width:260px; line-height:1.4">
              <div style="font-weight:600;margin-bottom:4px;">Наряд №${
                taskDetails.id
              }</div>
              <div><strong>Тип наряда:</strong> ${
                taskDetails.taskType || '—'
              }</div>
              <div><strong>Статус наряда:</strong> ${
                taskDetails.taskStatus || '—'
              }</div>
              <div><strong>Приоритет:</strong> ${priority}</div>
              <div><strong>Исполнители:</strong> ${employeesPreview}${extraEmployees}</div>
              <div><strong>Название объекта:</strong> ${
                taskDetails.asset?.name || 'Без названия'
              }</div>
              <div><strong>Адрес:</strong> ${
                taskDetails.asset?.address || 'Не указан'
              }</div>
              <div><strong>Координаты:</strong> ${taskDetails.asset
                ?.latitude}° N, ${taskDetails.asset?.longitude}° E</div>
            </div>
          `;

          const taskMarker = window.DG.marker(
            [taskDetails.asset.latitude, taskDetails.asset.longitude],
            {
              icon: createTaskMarkerIcon()
            }
          ).bindPopup(label);

          taskMarker.on('popupopen', () => {
            const popupElement = taskMarker.getPopup().getElement();
            popupElement
              ?.querySelector('.employees-btn')
              ?.addEventListener(
                'click',
                (event: { stopPropagation: () => void }) => {
                  event.stopPropagation();
                  if (
                    taskDetails.employees &&
                    taskDetails.employees.length > 0
                  ) {
                    onShowEmployees?.(taskDetails.employees);
                  }
                }
              );
            popupElement
              ?.querySelector('.marker-btn')
              ?.addEventListener(
                'click',
                (event: { stopPropagation: () => void }) => {
                  event.stopPropagation();
                }
              );
          });

          taskMarker.addTo(markersLayerRef.current);

          bounds.extend([
            taskDetails.asset.latitude,
            taskDetails.asset.longitude
          ]);
        }

        transitionsWithCoordinates.forEach((transition) => {
          if (transition.visibleOnMap === false) {
            return;
          }

          const label = `
            <div style="min-width:320px; line-height:1.5">
              <div style="font-weight:600;margin-bottom:4px;">Переход №${
                transition.order
              }</div>
              <div><strong>Статус до:</strong> ${
                transition.fromStatus?.label || '-'
              }</div>
              <div><strong>Статус после:</strong> ${
                transition.toStatus?.label || '-'
              }</div>
              <div><strong>Дата:</strong> ${formatDateStringFromRaw(
                transition.date
              )}</div>
              ${
                transition.createdBy?.label
                  ? `<div><strong>Исполнитель:</strong> ${transition.createdBy.label}</div>`
                  : ''
              }
              <div><strong>Координаты:</strong> ${transition.latitude}° N, ${
                transition.longitude
              }° E</div>
              ${
                transition.closingCode?.label
                  ? `<div><strong>Код закрытия:</strong> ${transition.closingCode.label}</div>`
                  : ''
              }
            </div>
          `;

          const transitionMarker = window.DG.marker(
            [transition.latitude, transition.longitude],
            {
              icon: createNumberedTransitionIcon(transition.order)
            }
          ).bindPopup(label);

          transitionMarker.on('popupopen', () => {
            const popupElement = transitionMarker.getPopup().getElement();
            popupElement
              ?.querySelector('.marker-btn')
              ?.addEventListener(
                'click',
                (event: { stopPropagation: () => void }) => {
                  event.stopPropagation();
                }
              );
          });

          transitionMarker.addTo(markersLayerRef.current);

          bounds.extend([transition.latitude, transition.longitude]);
        });

        // Add employee markers to cluster
        employeesWithCoordinates.forEach((employee) => {
          const label = `
            <div style="min-width:260px; line-height:1.4">
              <div style="font-weight:600;margin-bottom:4px;">Исполнитель</div>
              <div><strong>ФИО:</strong> ${employee.fullName || '—'}</div>
              <div><strong>Время обновления координат:</strong> ${
                employee.lastCoordinateUpdate
                  ? formatDateStringFromRaw(employee.lastCoordinateUpdate)
                  : '—'
              }</div>
              <div><strong>Координаты:</strong> ${employee.latitude}° N, ${
                employee.longitude
              }° E</div>
            </div>
          `;

          const employeeMarker = window.DG.marker(
            [employee.latitude, employee.longitude],
            {
              icon: createEmployeeMarkerIcon()
            }
          ).bindPopup(label);

          employeeMarker.on('popupopen', () => {
            const popupElement = employeeMarker.getPopup().getElement();
            popupElement
              ?.querySelector('.marker-btn')
              ?.addEventListener(
                'click',
                (event: { stopPropagation: () => void }) => {
                  event.stopPropagation();
                }
              );
          });

          employeeClusterRef.current.addLayer(employeeMarker);

          bounds.extend([employee.latitude, employee.longitude]);
        });

        const visibleTransitions = transitionsWithCoordinates.filter(
          (transition) => transition.visibleOnMap !== false
        );
        if (visibleTransitions.length > 1) {
          const routePoints = visibleTransitions.map((transition) => [
            transition.latitude,
            transition.longitude
          ]);

          window.DG.polyline(routePoints, {
            color: '#059669',
            weight: 4,
            dashArray: '6 6'
          }).addTo(routeLayerRef.current);
        }

        if (bounds.isValid()) {
          mapRef.current.fitBounds(bounds, { padding: [30, 30] });
        }
      });
    };

    if (!window.DG) {
      const script = document.createElement('script');
      script.src = 'https://maps.api.2gis.ru/2.0/loader.js?pkg=full';
      script.async = true;
      document.head.appendChild(script);
      script.onload = () => initializeMap();

      return () => {
        document.head.removeChild(script);
      };
    }

    initializeMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      if (markersLayerRef.current) {
        markersLayerRef.current.clearLayers();
        markersLayerRef.current = null;
      }
      if (employeeClusterRef.current) {
        employeeClusterRef.current.clearLayers();
        employeeClusterRef.current = null;
      }
      if (routeLayerRef.current) {
        routeLayerRef.current.clearLayers();
        routeLayerRef.current = null;
      }
    };
  }, [
    taskDetails,
    transitionsWithCoordinates,
    employeesWithCoordinates,
    containerId,
    reservedRightWidth,
    onShowEmployees
  ]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div id={containerId} style={{ width: '100%', height: '100%' }}></div>
      <style>
        {`          
          .employee-cluster {
            background-color: rgba(0, 0, 255, 0.6);
            border-radius: 50%;
            text-align: center;
            color: white;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}
      </style>
    </div>
  );
};

export default TaskStatusMap;
